import React, { useState } from 'react';
import { SlidersHorizontal, Link } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTrigger,
} from "./sheet";
import { Button } from "./button";
import { Label } from "./label";
import { Slider } from "./slider";
import { Checkbox } from "./checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./Select";
import { CSS4_COLORS } from './mplcolors';
import { Bike, Building2, Trees, Mountain, Waves, Droplets, Car, LandPlot } from 'lucide-react';
import { Input } from "./input";

const ACTIVITY_TYPES = ['Run', 'Ride', 'Hike', 'Walk', 'Swim', 'TrailRun', 'VirtualRide'];

const ColorSettingsPopup = ({
  settings,
  onColorSettingChange,
  onActivityTypeSettingChange,
  triggerButton,
  syncOceansWater,
  onSyncToggle,
  onPaperstyleToggle,
  activitySource,
  selectedCategory: selectedCategoryProp,
  onSelectedCategoryChange,
  onShowAddActivities,
}) => {
  const categories = ['activities', 'land', 'buildings', 'streets', 'forests', 'naturalareas', 'oceans', 'water'];
  const [sync, setSync] = useState(false || syncOceansWater);
  const [activityTab, setActivityTab] = useState(ACTIVITY_TYPES[0]);
  const [selectedCategoryLocal, setSelectedCategoryLocal] = useState(null);
  const selectedCategory = selectedCategoryProp !== undefined ? selectedCategoryProp : selectedCategoryLocal;
  const setSelectedCategory = onSelectedCategoryChange || setSelectedCategoryLocal;
  const resolveHex = (colorStr) => {
    if (!colorStr) return '#cccccc';
    if (colorStr.startsWith('#')) return colorStr;
    return CSS4_COLORS[colorStr] || '#cccccc';
  };

  // The header background is drawn semi-transparent on a white card; compute
  // the blended color for a contrast check so text is readable even when alpha
  // is low (otherwise white-on-nearly-white).
  const getHeaderColors = (hex, alpha) => {
    const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
    const a = Math.max(0, Math.min(1, typeof alpha === 'number' ? alpha : 1));
    if (!m) return { bg: hex, fg: '#111827' };
    const r = parseInt(m[1], 16);
    const g = parseInt(m[2], 16);
    const b = parseInt(m[3], 16);
    const bg = `rgba(${r}, ${g}, ${b}, ${a})`;
    const br = r * a + 255 * (1 - a);
    const bgBl = g * a + 255 * (1 - a);
    const bb = b * a + 255 * (1 - a);
    const lum = (0.299 * br + 0.587 * bgBl + 0.114 * bb) / 255;
    return { bg, fg: lum > 0.6 ? '#111827' : '#ffffff' };
  };

  const categoryIcons = {
    activities: Bike,
    land: LandPlot,
    buildings: Building2,
    forests: Trees,
    naturalareas: Mountain,
    oceans: Waves,
    water: Droplets,
    streets: Car,
  };

  // Opacity "sweet spots" per category — rendered as a subtle green band on
  // the opacity slider so the user can see the recommended range at a glance.
  // Rationale: too-high land/forest coverage hides the topography underneath.
  const opacityOptimalRanges = {
    activities: [50, 100],
    land: [5, 60],
    forests: [5, 45],
    oceans: [10, 100],
    water: [10, 100],
    buildings: [5, 100],
    streets: [10, 80],
  };

  const handleCustomColorClick = (category) => {
    const input = document.createElement('input');
    input.type = 'color';
    input.value = settings[category].color;
    input.addEventListener('change', (e) => {
      onColorSettingChange(category, 'color', e.target.value);
      if (sync && category === 'oceans') {
        onColorSettingChange('water', 'color', e.target.value);
      }
    });
    input.click();
  };

  const getDisplayValue = (color) => {
    if (color && color.startsWith('#')) return `custom: ${color}`;
    return color;
  };

  const handleSliderChange = (category, value) => {
    onColorSettingChange(category, 'alpha', value[0] / 100);
    if (sync && category === 'oceans') {
      onColorSettingChange('water', 'alpha', value[0] / 100);
    }
  };

  const handleOpacityInputChange = (category, value) => {
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
      onColorSettingChange(category, 'alpha', numValue / 100);
      if (sync && category === 'oceans') {
        onColorSettingChange('water', 'alpha', numValue / 100);
      }
    }
  };

  const handleColorChange = (category, color) => {
    onColorSettingChange(category, 'color', color);
    if (sync && category === 'oceans') {
      onColorSettingChange('water', 'color', color);
    }
  };

  // Open a native color picker that writes back through an arbitrary setter —
  // used for per-activity-type tabs so the "custom" option persists to byType.
  const openCustomColorPicker = (initialColor, setColor) => {
    const input = document.createElement('input');
    input.type = 'color';
    input.value = initialColor && initialColor.startsWith('#') ? initialColor : '#ff0000';
    input.addEventListener('change', (e) => setColor(e.target.value));
    input.click();
  };

  // Render the shared color / opacity / line-width controls from a resolved
  // scope and a single write function. Used for both the common activities
  // settings and per-type overrides.
  const renderActivityStyleControls = ({ color, alpha, onChange, rightSlot = null }) => {
    return (
      <div className="space-y-3">
        <div className="space-y-1">
          <Label className="text-xs">Color</Label>
          <div className="flex items-center gap-2">
          <div className="flex-1 min-w-0">
          <Select
            value={color}
            onValueChange={(value) => {
              if (value === 'custom') {
                openCustomColorPicker(color, (c) => onChange('color', c));
                return;
              }
              onChange('color', value);
            }}
          >
            <SelectTrigger className="bg-white border border-none rounded-lg h-9 text-sm">
              <SelectValue>
                <div className="flex items-center">
                  <div className="w-4 h-4 mr-2 rounded-full" style={{ backgroundColor: color }} />
                  {getDisplayValue(color)}
                </div>
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <div className="max-h-96 overflow-y-auto">
                <SelectItem
                  value="custom"
                  className="sticky top-0 z-10 bg-white border-b border-gray-200"
                >
                  <div className="flex items-center">
                    <div
                      className="w-4 h-4 mr-2 rounded-full"
                      style={{ background: 'linear-gradient(to right, red,orange,yellow,green,blue,indigo,violet)' }}
                    />
                    custom
                  </div>
                </SelectItem>
                {Object.keys(CSS4_COLORS).map((c) => (
                  <SelectItem key={c} value={c}>
                    <div className="flex items-center">
                      <div className="w-4 h-4 mr-2 rounded-full" style={{ backgroundColor: c }} />
                      {c}
                    </div>
                  </SelectItem>
                ))}
              </div>
            </SelectContent>
          </Select>
          </div>
          {rightSlot}
          </div>
        </div>

        <div className="space-y-1">
          <div className="flex items-baseline justify-between gap-2">
            <Label className="text-xs">Opacity ({Math.round(alpha * 100)}%)</Label>
            {opacityOptimalRanges.activities && (
              <span className="text-xs text-green-700">
                Optimal: {opacityOptimalRanges.activities[0]}–{opacityOptimalRanges.activities[1]}%
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-grow relative">
              {opacityOptimalRanges.activities && (
                <div
                  className="pointer-events-none absolute top-1/2 -translate-y-1/2 h-7 bg-green-400/70 rounded-md z-0 ring-1 ring-green-500/60"
                  style={{
                    left: `${opacityOptimalRanges.activities[0]}%`,
                    width: `${opacityOptimalRanges.activities[1] - opacityOptimalRanges.activities[0]}%`,
                  }}
                  aria-hidden="true"
                />
              )}
              <Slider
                value={[alpha * 100]}
                min={0}
                max={100}
                step={1}
                onValueChange={(value) => onChange('alpha', value[0] / 100)}
                className="relative z-10 border-2 border-white rounded-lg"
              />
            </div>
            <Input
              type="number"
              min={0}
              max={100}
              value={Math.round(alpha * 100)}
              onChange={(e) => {
                const n = parseInt(e.target.value, 10);
                if (!isNaN(n) && n >= 0 && n <= 100) onChange('alpha', n / 100);
              }}
              className="w-14 h-8 text-center text-sm"
            />
          </div>
        </div>
      </div>
    );
  };

  const renderActivityBody = () => {
    const activities = settings.activities || {};
    const byTypeEnabled = !!activities.byTypeEnabled;

    const commonScope = {
      color: activities.color,
      alpha: activities.alpha,
    };

    const commonOnChange = (setting, value) => onColorSettingChange('activities', setting, value);

    const canEditPerType = byTypeEnabled && typeof onActivityTypeSettingChange === 'function';
    const typeOverrides = activities.byType?.[activityTab] || {};
    const typeScope = {
      color: typeOverrides.color || activities.color,
      alpha: typeOverrides.alpha != null ? typeOverrides.alpha : activities.alpha,
    };
    const typeOnChange = (setting, value) => onActivityTypeSettingChange(activityTab, setting, value);

    const perTypeToggle = typeof onActivityTypeSettingChange === 'function' ? (
      <div className="flex items-center gap-1.5 shrink-0">
        <Label htmlFor="byTypeEnabled" className="text-[11px] cursor-pointer leading-tight text-right">
          By type
        </Label>
        <Checkbox
          id="byTypeEnabled"
          checked={byTypeEnabled}
          onCheckedChange={(checked) => onColorSettingChange('activities', 'byTypeEnabled', !!checked)}
          className="h-5 w-5 rounded-full border-black"
          title="Choose different colors per activity type"
        />
      </div>
    ) : null;

    // When byType is on, the type picker sits inline above the controls as
    // a compact Select dropdown instead of a row of tab buttons. Each option
    // gets a small blue dot if that type has its own override, so the user
    // can still tell at a glance which types differ from the common style.
    const typePickerSlot = canEditPerType ? (
      <div className="flex items-center gap-1.5 shrink-0">
        <Label className="text-[11px] leading-tight">Type</Label>
        <Select value={activityTab} onValueChange={setActivityTab}>
          <SelectTrigger className="bg-white border border-gray-200 rounded-lg h-7 text-xs px-2 w-24">
            <SelectValue>
              <span className="flex items-center gap-1">
                {activityTab}
                {!!activities.byType?.[activityTab] && (
                  <span className="text-[10px] text-blue-600">●</span>
                )}
              </span>
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {ACTIVITY_TYPES.map((type) => (
              <SelectItem key={type} value={type}>
                <span className="flex items-center gap-1">
                  {type}
                  {!!activities.byType?.[type] && (
                    <span className="text-[10px] text-blue-600">●</span>
                  )}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    ) : null;

    // Both the "By type" toggle and (when byType is on) the type picker
    // share the slot next to the color dropdown. The toggle stays visible
    // in both modes so the user can always turn byType back off.
    const rightSlot = (
      <div className="flex items-center gap-3 shrink-0">
        {typePickerSlot}
        {perTypeToggle}
      </div>
    );

    return (
      <>
        {canEditPerType ? (
          <>
            {renderActivityStyleControls({
              ...typeScope,
              onChange: typeOnChange,
              rightSlot,
            })}
            {activities.byType?.[activityTab] && (
              <div className="mt-2 text-right">
                <button
                  type="button"
                  onClick={() => onActivityTypeSettingChange(activityTab, '__reset__', null)}
                  className="text-xs text-gray-500 hover:text-gray-800 underline"
                >
                  Reset {activityTab} to common
                </button>
              </div>
            )}
          </>
        ) : (
          renderActivityStyleControls({
            ...commonScope,
            onChange: commonOnChange,
            rightSlot,
          })
        )}
      </>
    );
  };

  const getCategoryMeta = (category) => {
    const isActive = !!settings[category].active;
    const isWaterSynced = sync && category === 'water';
    const isActivitiesDisabled = category === 'activities' && activitySource === 'none';
    const isBuildingsBig = category === 'buildings' && settings.km2 > 15000;
    const isStreetsBig = category === 'streets' && settings.km2 > 15000;
    // Paperstyle forces buildings off — same UX as the >15000 km² lock:
    // toggle disabled, expanded tile shows a red warning instead of the
    // color/opacity body, no "turn on the toggle" hint.
    const isBuildingsPaperstyle = category === 'buildings' && !!settings.paperstyle;
    const blocked = isWaterSynced || isActivitiesDisabled;
    const toggleDisabled = blocked || isBuildingsBig || isStreetsBig || isBuildingsPaperstyle;
    const hex = resolveHex(settings[category].color);
    const showColored = isActive && !blocked;
    const headerColors = showColored
      ? getHeaderColors(hex, settings[category].alpha)
      : { bg: '#9ca3af', fg: '#ffffff' };
    return {
      isActive,
      isWaterSynced,
      isActivitiesDisabled,
      isBuildingsBig,
      isStreetsBig,
      isBuildingsPaperstyle,
      blocked,
      toggleDisabled,
      hex,
      headerBg: headerColors.bg,
      headerFg: headerColors.fg,
    };
  };

  const renderCategoryHeader = (category) => {
    const meta = getCategoryMeta(category);
    const Icon = categoryIcons[category];
    const showLink = sync && (category === 'oceans' || category === 'water');
    return (
      <div
        className="px-2 py-1.5 flex items-center gap-2"
        style={{ backgroundColor: meta.headerBg, color: meta.headerFg }}
      >
        <Icon className="h-4 w-4 flex-shrink-0" />
        <span className="text-sm font-semibold capitalize truncate">{category}</span>
        {showLink && (
          <Link className="h-3.5 w-3.5 flex-shrink-0" title="Linked with water / oceans" />
        )}
        <div
          className="ml-auto flex items-center"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <Checkbox
            checked={meta.isActive}
            disabled={meta.toggleDisabled}
            onCheckedChange={(checked) => {
              onColorSettingChange(category, 'active', checked);
              if (sync && category === 'oceans') {
                onColorSettingChange('water', 'active', checked);
              }
            }}
            className="h-5 w-5 rounded-full border-current"
          />
        </div>
      </div>
    );
  };

  const renderCategoryTile = (category) => {
    const meta = getCategoryMeta(category);

    let titleHint = `Edit ${category}`;
    if (meta.isActivitiesDisabled) titleHint = 'Select an activity source';
    else if (meta.isWaterSynced) titleHint = 'Synced with oceans';
    else if (meta.isBuildingsBig || meta.isStreetsBig) titleHint = 'Area too large';

    const handleClick = () => setSelectedCategory(category);

    return (
      <div
        role="button"
        tabIndex={meta.blocked ? -1 : 0}
        onClick={() => { if (!meta.blocked) handleClick(); }}
        onKeyDown={(e) => {
          if (!meta.blocked && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            handleClick();
          }
        }}
        className={`h-full flex flex-col overflow-hidden rounded-xl border shadow-sm transition group ${meta.blocked
          ? 'opacity-60 cursor-not-allowed border-gray-300'
          : 'cursor-pointer hover:shadow-md hover:scale-[1.02] border-transparent'
          } ${!meta.isActive && !meta.blocked ? 'saturate-50' : ''}`}
        title={titleHint}
      >
        {renderCategoryHeader(category)}
        <div className={`relative flex-1 aspect-[4/3] bg-gray-100 overflow-hidden ${!meta.isActive ? 'grayscale opacity-80' : ''}`}>
          <img
            src={`/assets/custom/${category}.png`}
            alt={category}
            className="w-full h-full object-cover"
            onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
          />
          {meta.isActivitiesDisabled && onShowAddActivities && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-2 text-center bg-white/80">
              <p className="text-xs font-semibold text-gray-800">
                Need to add activities first
              </p>
              <Button
                variant="outline"
                size="sm"
                className="rounded-2xl whitespace-nowrap pointer-events-auto"
                onClick={(e) => { e.stopPropagation(); onShowAddActivities(); }}
              >
                Show me
              </Button>
            </div>
          )}
          {!meta.blocked && (
            <div className="absolute bottom-2 right-2 bg-white bg-opacity-90 rounded-full p-1.5 shadow-md opacity-80 group-hover:opacity-100 transition-opacity">
              <SlidersHorizontal className="h-3.5 w-3.5 text-gray-700" />
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderCollapsedTile = (category) => {
    const meta = getCategoryMeta(category);
    const isSelected = selectedCategory === category;
    const handleClick = () => setSelectedCategory(category);
    let titleHint = `Open ${category}`;
    if (meta.isActivitiesDisabled) titleHint = 'Select an activity source';
    else if (meta.isWaterSynced) titleHint = 'Synced with oceans';
    else if (meta.isBuildingsBig || meta.isStreetsBig) titleHint = 'Area too large';

    return (
      <div
        role="button"
        tabIndex={meta.blocked ? -1 : 0}
        onClick={() => { if (!meta.blocked) handleClick(); }}
        onKeyDown={(e) => {
          if (!meta.blocked && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            handleClick();
          }
        }}
        className={`rounded-xl overflow-hidden border shadow-sm transition ${meta.blocked
          ? 'opacity-60 cursor-not-allowed border-gray-300'
          : 'cursor-pointer hover:shadow-md'
          } ${isSelected ? 'border-gray-900 ring-2 ring-gray-900' : 'border-transparent'} ${!meta.isActive && !meta.blocked ? 'saturate-50' : ''}`}
        title={titleHint}
      >
        {renderCategoryHeader(category)}
      </div>
    );
  };

  const renderExpandedTile = (category) => {
    const meta = getCategoryMeta(category);
    const sizeWarning = meta.isBuildingsBig
      ? 'Buildings disabled for areas bigger than 15000 km²'
      : meta.isStreetsBig
        ? 'Streets disabled for areas bigger than 15000 km²'
        : meta.isBuildingsPaperstyle
          ? 'Buildings disabled while Hand-painted paper look is on'
          : null;

    return (
      <div className="flex flex-col overflow-hidden rounded-xl border border-gray-200 shadow-lg bg-white">
        {renderCategoryHeader(category)}
        <div className="p-3 space-y-2">
          {meta.isWaterSynced && (
            <div className="bg-blue-100 text-blue-800 px-3 py-2 rounded-xl text-sm flex items-center gap-2 justify-center">
              <Link className="h-4 w-4" />
              <span className="font-medium">Synced with oceans — controlled by the oceans category</span>
              <button
                type="button"
                onClick={() => { if (onSyncToggle) onSyncToggle(false); setSync(false); }}
                className="ml-2 text-[11px] underline hover:text-blue-900"
              >
                Unsync
              </button>
            </div>
          )}
          {meta.isActivitiesDisabled && (
            <div className="text-sm text-gray-600 text-center py-4">
              Select an activity source to enable activities.
            </div>
          )}
          {sizeWarning && (
            <div className="text-red-500 text-sm text-center">{sizeWarning}</div>
          )}
          {meta.isActive && !meta.isActivitiesDisabled && !meta.isWaterSynced ? (
            category === 'activities' ? renderActivityBody() : renderNonActivityBody(category)
          ) : (!meta.isActivitiesDisabled && !meta.isWaterSynced && !meta.isBuildingsPaperstyle && (
            // Skip the "turn on the toggle" hint when the toggle is locked
            // off by paperstyle — the user can't turn it on, so the hint
            // would be misleading. The red warning above already explains.
            <div className="text-sm text-gray-500 text-center py-4">
              Turn on the toggle in the header to edit settings.
            </div>
          ))}
        </div>
        {/* In Basic mode the activities tile is rendered inline (not as
            an expanded view inside a category grid), so a "Done" button
            has nothing to dismiss. */}
        {!renderModeIsBasic && (
          <div className="border-t bg-gray-50">
            <Button
              onClick={() => setSelectedCategory(null)}
              className="w-full bg-gray-900 border-0 text-white hover:bg-gray-600 rounded-none"
            >
              Done
            </Button>
          </div>
        )}
      </div>
    );
  };

  const renderNonActivityBody = (category) => {
    // Sync chip lives next to the color dropdown for the oceans/water pair
    // so both cards share the same vertical layout as the activities card.
    // Toggling on either copies oceans→water once, so the synced category
    // visually updates immediately even though oceans is the source of
    // truth from this point on.
    const handleSyncToggle = (checked) => {
      if (onSyncToggle) onSyncToggle(checked);
      setSync(checked);
      if (checked) {
        onColorSettingChange('water', 'color', settings.oceans.color);
        onColorSettingChange('water', 'alpha', settings.oceans.alpha);
        onColorSettingChange('water', 'active', settings.oceans.active);
      }
    };
    const syncSlot = (category === 'oceans' || category === 'water') ? (
      <div className="flex items-center gap-1.5 shrink-0">
        <Label htmlFor={`syncCheckbox-${category}`} className="text-[11px] cursor-pointer leading-tight text-right flex items-center gap-1">
          {category === 'oceans' ? 'Sync water' : 'Sync oceans'}
          <Link className="h-3 w-3" />
        </Label>
        <Checkbox
          id={`syncCheckbox-${category}`}
          checked={sync}
          onCheckedChange={handleSyncToggle}
          className="h-5 w-5 rounded-full border-black"
        />
      </div>
    ) : null;

    return (
    <div className="space-y-3">
      <div className="space-y-1">
        <Label className="text-xs">Color</Label>
        <div className="flex items-center gap-2">
          <div className="flex-1 min-w-0">
        <Select
          value={settings[category].color}
          onValueChange={(value) => {
            if (value === 'custom') {
              handleCustomColorClick(category);
              return;
            }
            handleColorChange(category, value);
          }}
        >
          <SelectTrigger className="bg-white border border-none rounded-lg h-9 text-sm">
            <SelectValue>
              <div className="flex items-center">
                <div
                  className="w-4 h-4 mr-2 rounded-full"
                  style={{ backgroundColor: settings[category].color }}
                />
                {getDisplayValue(settings[category].color)}
              </div>
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <div className="max-h-96 overflow-y-auto">
              <SelectItem
                value="custom"
                className="sticky top-0 z-10 bg-white border-b border-gray-200"
              >
                <div className="flex items-center">
                  <div
                    className="w-4 h-4 mr-2 rounded-full"
                    style={{ background: 'linear-gradient(to right, red,orange,yellow,green,blue,indigo,violet)' }}
                  />
                  custom
                </div>
              </SelectItem>
              {Object.keys(CSS4_COLORS).map((color) => (
                <SelectItem key={color} value={color}>
                  <div className="flex items-center">
                    <div
                      className="w-4 h-4 mr-2 rounded-full"
                      style={{ backgroundColor: color }}
                    />
                    {color}
                  </div>
                </SelectItem>
              ))}
            </div>
          </SelectContent>
        </Select>
          </div>
          {syncSlot}
        </div>
      </div>
      <div className="space-y-1">
        <div className="flex items-baseline justify-between gap-2">
          <Label className="text-xs">Opacity ({Math.round(settings[category].alpha * 100)}%)</Label>
          {opacityOptimalRanges[category] && (
            <span className="text-xs text-green-700">
              Optimal: {opacityOptimalRanges[category][0]}–{opacityOptimalRanges[category][1]}%
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex-grow relative">
            {opacityOptimalRanges[category] && (
              <div
                className="pointer-events-none absolute top-1/2 -translate-y-1/2 h-7 bg-green-400/70 rounded-md z-0 ring-1 ring-green-500/60"
                style={{
                  left: `${opacityOptimalRanges[category][0]}%`,
                  width: `${opacityOptimalRanges[category][1] - opacityOptimalRanges[category][0]}%`,
                }}
                aria-hidden="true"
              />
            )}
            <Slider
              value={[settings[category].alpha * 100]}
              min={0}
              max={100}
              step={1}
              onValueChange={(value) => handleSliderChange(category, value)}
              className="relative z-10 border-2 border-white rounded-lg"
            />
          </div>
          <Input
            type="number"
            min={0}
            max={100}
            value={Math.round(settings[category].alpha * 100)}
            onChange={(e) => handleOpacityInputChange(category, e.target.value)}
            className="w-14 h-8 text-center text-sm"
          />
        </div>
      </div>
    </div>
    );
  };

  // The popup body adapts to settings.renderMode set at the dashboard level:
  //   - 'features' (or undefined): the existing per-category grid + expand
  //     view stays unchanged.
  //   - 'basic': only Activities is customizable, so we render the expanded
  //     activities tile. Tab switching itself happens outside the popup.
  const renderModeIsBasic = settings?.renderMode === 'basic';

  const renderBasicContent = () => (
    <div className="py-2 w-full max-w-xl mx-auto">
      {renderExpandedTile('activities')}
    </div>
  );

  // Top-level "paper style" toggle (Stamen watercolor-derived post-
  // process). It's a render-mode flag, not a per-category color, so it
  // lives outside the category grid — rendered *below* the grid so the
  // primary controls (color/alpha for each layer) stay above the fold.
  // Toggling on also forces buildings off because the wobble pipeline
  // can't handle small dense polygons cleanly — they render as speckle.
  const renderPaperstyleToggle = () => {
    if (!onPaperstyleToggle) return null;
    const isOn = !!settings?.paperstyle;
    return (
      <div className="flex items-start gap-3 px-2 py-2 mt-3 rounded-lg border border-gray-200 bg-gray-50">
        <Checkbox
          id="paperstyleToggle"
          checked={isOn}
          onCheckedChange={(checked) => {
            const next = checked === true;
            onPaperstyleToggle(next);
            // Force buildings off when paper style is enabled — see comment
            // above. We restore nothing on disable; the user can re-enable
            // buildings manually if they want them back.
            if (next && onColorSettingChange) {
              onColorSettingChange('buildings', 'active', false);
            }
          }}
          className="h-5 w-5 rounded-full border-black mt-0.5"
        />
        <Label htmlFor="paperstyleToggle" className="text-xs cursor-pointer">
          <span className="font-semibold">Hand-painted paper look</span>
          <span className="block text-[11px] text-gray-500 mt-0.5">
            (disables buildings)
          </span>
        </Label>
      </div>
    );
  };

  const renderFeaturesContent = () => {
    if (selectedCategory) {
      // Right column stretches to match the left list (items-stretch) and
      // shares the same gap-2 as the left, so flex 5:3 between card and
      // image lines up the seam with the left's entry/entry gap. Card
      // occupies the top 5 entries, image fills the bottom 3.
      return (
        <div>
          <div className="flex gap-3 py-2 items-stretch">
            <div className="flex flex-col gap-2 w-28 md:w-44 shrink-0">
              {categories.map((c) => (
                <div key={c}>{renderCollapsedTile(c)}</div>
              ))}
            </div>
            <div className="flex-1 min-w-0 flex flex-col gap-2">
              <div className="w-full max-w-xl mx-auto" style={{ flex: '5 1 0' }}>
                {renderExpandedTile(selectedCategory)}
              </div>
              <div
                className="rounded-xl overflow-hidden shadow-sm w-full max-w-xl mx-auto"
                style={{ flex: '3 1 0' }}
              >
                <img
                  src={`/assets/custom/${selectedCategory}.png`}
                  alt=""
                  aria-hidden="true"
                  className="w-full h-full object-cover"
                  onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }}
                />
              </div>
            </div>
          </div>
          {renderPaperstyleToggle()}
        </div>
      );
    }
    return (
      <div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 py-2">
          {categories.map((category) => (
            <div key={category}>{renderCategoryTile(category)}</div>
          ))}
        </div>
        {renderPaperstyleToggle()}
      </div>
    );
  };

  const renderContent = () =>
    renderModeIsBasic ? renderBasicContent() : renderFeaturesContent();

  if (triggerButton) {
    return (
      <Sheet>
        <SheetTrigger asChild>
          <Button
            size="sm"
            className="text-xs lg:text-md rainbow-border-bg text-white hover:bg-gray-600"
          >
            <SlidersHorizontal className="h-4 w-4 mr-2" />
            Customize
          </Button>
        </SheetTrigger>
        <SheetContent className="w-full overflow-y-auto">
          <SheetHeader>
            {/* <SheetTitle>Color Settings</SheetTitle> */}
          </SheetHeader>
          {renderContent()}
        </SheetContent>
      </Sheet>
    );
  }

  return renderContent();
};

export default ColorSettingsPopup;
